import { useState } from 'react'
import { Wifi, AlertCircle, CheckCircle, Shield, Activity, Clock, Download, Monitor } from 'lucide-react'

export default function GuardbotIntegrationPage() {
  const [guardbotConnected, setGuardbotConnected] = useState(false)

  const handleConnectGuardbot = () => {
    setGuardbotConnected(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Guardbot System Integration</h1>
        <p className="text-blue-200">Connect & Protect Your Device with Guardbot</p>
      </div>

      {/* Connection Status */}
      <div className={`rounded-lg border-2 p-8 mb-8 transition-all ${
        guardbotConnected ? 'border-green-500 bg-green-500/10' : 'border-yellow-500 bg-yellow-500/10'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white">Connection Status</h2>
          {guardbotConnected ? (
            <CheckCircle size={40} className="text-green-500" />
          ) : (
            <AlertCircle size={40} className="text-yellow-500" />
          )}
        </div>
        <p className={`text-lg font-bold mb-2 ${guardbotConnected ? 'text-green-400' : 'text-yellow-400'}`}>
          {guardbotConnected ? 'Connected & Protected' : 'Not Connected'}
        </p>
        <p className="text-blue-200">
          {guardbotConnected
            ? 'Your device is fully protected by Guardbot. Real-time threat detection is active.'
            : 'Connect to Guardbot to enable real-time threat detection and device protection.'
          }
        </p>
      </div>

      {guardbotConnected ? (
        <>
          {/* Device Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-200">Protection Level</p>
                <Shield size={20} className="text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-400">Maximum</p>
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-200">Real-time Scanning</p>
                <Activity size={20} className="text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-blue-400">Active</p>
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-200">Threats Detected</p>
                <AlertCircle size={20} className="text-yellow-500" />
              </div>
              <p className="text-2xl font-bold text-white">0</p>
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-200">Last Updated</p>
                <Clock size={20} className="text-purple-500" />
              </div>
              <p className="text-2xl font-bold text-white">2 min ago</p>
            </div>
          </div>

          {/* Protected Systems */}
          <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Monitor size={20} />
              <span>Protected Systems</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div>
                  <p className="font-semibold text-white">This Application</p>
                  <p className="text-xs text-green-300">App-level Security: Active</p>
                </div>
                <CheckCircle size={24} className="text-green-500" />
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div>
                  <p className="font-semibold text-white">System Protection</p>
                  <p className="text-xs text-green-300">Device-level: Active</p>
                </div>
                <CheckCircle size={24} className="text-green-500" />
              </div>
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div>
                  <p className="font-semibold text-white">Network Monitoring</p>
                  <p className="text-xs text-green-300">Real-time Monitoring: Active</p>
                </div>
                <CheckCircle size={24} className="text-green-500" />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Connection Guide */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">System Protection Benefits</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Real-time Threat Detection</p>
                    <p className="text-sm text-blue-200">24/7 monitoring of your system</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Malware Removal</p>
                    <p className="text-sm text-blue-200">Automatic threat elimination</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Incident Response</p>
                    <p className="text-sm text-blue-200">Immediate automated response</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">App Protection Benefits</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Input Validation</p>
                    <p className="text-sm text-blue-200">Prevent injection attacks</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Intrusion Prevention</p>
                    <p className="text-sm text-blue-200">Block unauthorized access</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Data Protection</p>
                    <p className="text-sm text-blue-200">Encrypt sensitive data</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Download Links */}
          <div className="bg-slate-800/50 rounded-lg border border-green-500/30 p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
              <Download size={20} />
              <span>Download Guardbot from Your Platform</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.cybertection.androidavnew"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🤖</span>
                <span className="text-white font-semibold">Google Play (Guardbot AI)</span>
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.cybertection.cybertectiondeepfakedetector"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🔍</span>
                <span className="text-white font-semibold">Google Play (Deepfake Scan)</span>
              </a>

              <a
                href="https://apps.microsoft.com/detail/9NKQWD1G0C0C?hl=en-us&gl=US&ocid=pdpshare"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🪟</span>
                <span className="text-white font-semibold">Microsoft Store</span>
              </a>

              <a
                href="https://cybertection-guardbot.com/windows"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">📥</span>
                <span className="text-white font-semibold">Windows Direct Download</span>
              </a>

              <a
                href="https://snapcraft.io/cybertection-guardbot"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🐧</span>
                <span className="text-white font-semibold">Linux Snapcraft</span>
              </a>

              <a
                href="https://www.amazon.com/gp/product/B0GDVY9Y1V"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🛒</span>
                <span className="text-white font-semibold">Amazon App Store</span>
              </a>

              <a
                href="https://expo.dev/accounts/joebuun/projects/CybertectionGuardbotIOS/builds/299e15ca-4b56-4bd6-b3a7-203f070be6bc"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🥽</span>
                <span className="text-white font-semibold">Meta App Store</span>
              </a>

              <a
                href="https://www.cybertection.net/category/all-products"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg hover:border-green-500/60 hover:bg-green-500/20 transition-all"
              >
                <span className="text-2xl">🛡️</span>
                <span className="text-white font-semibold">Cybertection Store</span>
              </a>
            </div>
          </div>

          {/* Connect Button */}
          <button
            onClick={handleConnectGuardbot}
            className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold text-lg hover:shadow-lg hover:shadow-green-500/50 transition-all flex items-center justify-center space-x-2"
          >
            <Wifi size={24} />
            <span>Connect Guardbot Now</span>
          </button>
        </>
      )}
    </div>
  )
}
